import { formatters } from '@Components/hierarchical-report/Layout'

import { AUTO_INTERVAL } from './chartIntervals'
import { axisAcceptsAnyGroupby, collapsesChartIndex, getXAxisFields, isAxisFieldAllowed } from './chartUtils'
import { getCollectionGroupbyOptions, getGroupbyFieldLabel } from './collectionGroupby'
import { getGroupedCollectionMetrics, getMetricDisplayLabel } from './collectionMetrics'
import { DEFAULT_COLLECTION, createDefaultReportConfigForCollection } from './defaultReports'

export { COLLECTIONS } from './collectionMetrics'

export const CHART_TYPES = [
  { value: 'line-chart', label: 'Line chart' },
  { value: 'bar-chart', label: 'Stacked bar chart' },
  { value: 'pie-chart', label: 'Pie chart' },
  { value: 'funnel-chart', label: 'Funnel chart' },
]

export const CHART_SIZE_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'half', label: 'Half' },
]

export function getChartColSpanClass(layoutSize = 'full') {
  return layoutSize === 'half' ? 'col-span-12 lg:col-span-6' : 'col-span-12'
}

export const SORTBY_OPTIONS = [
  { value: 'total desc', label: 'Total desc', group: 'Total' },
  { value: 'total asc', label: 'Total asc', group: 'Total' },
  { value: 'uniqUser desc', label: 'Uniq User desc', group: 'Unique Users' },
  { value: 'uniqUser asc', label: 'Uniq User asc', group: 'Unique Users' },
  { value: 'revenue desc', label: 'Revenue desc', group: 'Revenue' },
  { value: 'revenue asc', label: 'Revenue asc', group: 'Revenue' },
]

export const SORT_DIRECTIONS = [
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
]

export function parseSortbyEntries(sortby = []) {
  return sortby.map(entry => {
    const lastSpace = entry.lastIndexOf(' ')
    if (lastSpace === -1) return { field: entry, direction: 'desc' }

    return {
      field: entry.slice(0, lastSpace),
      direction: entry.slice(lastSpace + 1) || 'desc',
    }
  })
}

export function formatSortbyEntries(entries = []) {
  return entries
    .filter(entry => entry.field)
    .map(({ field, direction }) => `${field} ${direction || 'desc'}`)
}

export function getSortFieldOptions(groupby = [], metrics = [], collection) {
  const options = []
  const seen = new Set()

  const addOption = (value, label, group) => {
    if (!value || seen.has(value)) return
    seen.add(value)
    options.push({ value, label, group })
  }

  groupby.forEach(field => {
    addOption(field, getGroupbyFieldLabel(collection, field), 'Group by')
  })

  metrics.forEach(metric => {
    if (metric?.key) {
      addOption(
        metric.key,
        getMetricDisplayLabel(collection, metric),
        'Metrics',
      )
    }
  })

  SORTBY_OPTIONS.forEach(option => {
    const field = option.value.split(' ')[0]
    addOption(field, formatters.groupsText(field), option.group)
  })

  return options
}

const FILTER_GROUP_ORDER = {
  Time: 1,
  Traffic: 2,
  Entity: 3,
  Total: 1,
  'Unique Users': 2,
  Revenue: 3,
  Other: 99,
}

export function getGroupedFilterOptions(options = []) {
  const grouped = {}

  options.forEach(option => {
    const group = option.group || 'Other'
    grouped[group] = grouped[group] || []
    grouped[group].push(option)
  })

  return Object.keys(grouped)
    .sort((a, b) => (FILTER_GROUP_ORDER[a] || 99) - (FILTER_GROUP_ORDER[b] || 99))
    .map(group => [group, grouped[group]])
}

export const createDefaultReportConfig = dates => createDefaultReportConfigForCollection(DEFAULT_COLLECTION, dates)

const DEFAULT_CHART_METRIC_LIMIT = 4

export const createDefaultChart = (config = {}) => {
  const configKeys = (config.metrics || []).map(metric => metric.key).filter(Boolean)
  const limited = configKeys.slice(0, DEFAULT_CHART_METRIC_LIMIT)
  const metricKeys = limited.length ? limited : ['total']

  return {
    name: '',
    dimensions: getDefaultChartDimensions(config.groupby, 'line-chart'),
    metricKeys,
    seriesBy: null,
    breakdown: metricKeys[0] || 'total',
    dataType: 'line-chart',
    layoutSize: 'full',
    interval: AUTO_INTERVAL,
    intervalQuantity: 1,
  }
}

export const DEFAULT_CHART_AXIS = 'day'

export function getDefaultChartDimensions(groupby = [], dataType) {
  const [timeField] = getXAxisFields(groupby)
  if (timeField) return [timeField]

  if (axisAcceptsAnyGroupby(dataType) && groupby.length) return groupby.slice(0, 1)

  return [DEFAULT_CHART_AXIS]
}

export function applyChartType(chart, dataType = 'line-chart', groupby = []) {
  const next = { ...chart, dataType }

  if (!collapsesChartIndex(dataType)) {
    const [dimension] = next.dimensions || []

    if (!isAxisFieldAllowed(dimension, dataType)) {
      next.dimensions = getDefaultChartDimensions(groupby, dataType)
    }

    return next
  }

  if (next.seriesBy && next.metricKeys?.length > 1) {
    const [firstKey] = next.metricKeys
    const valueKey = next.breakdown || firstKey

    next.metricKeys = [valueKey]
    next.breakdown = valueKey
  }

  return next
}

export function applyChartAxis(chart, field) {
  const dimensions = field ? [field] : []

  return {
    ...chart,
    dimensions,
    seriesBy: dimensions.includes(chart?.seriesBy) ? null : chart?.seriesBy,
  }
}

export function getChartTypeShortLabel(dataType) {
  const option = CHART_TYPES.find(type => type.value === dataType)

  return (option?.label || 'Chart').replace(/\s*chart$/i, '')
}

export function getChartDimensionOptions(collection, dataType) {
  return getCollectionGroupbyOptions(collection)
    .filter(option => isAxisFieldAllowed(option.value, dataType))
    .map(option => ({
      value: option.value,
      label: option.label,
      group: option.group,
    }))
}

export function getChartMetricOptions(collection) {
  return getGroupedCollectionMetrics(collection).flatMap(([group, metrics]) => metrics.map(metric => ({
    value: metric.key,
    label: metric.label,
    group,
  })))
}

export function getDefaultChartName(config = {}, metricKeys = []) {
  const keys = metricKeys.length
    ? metricKeys
    : config.metrics?.map(metric => metric.key).filter(Boolean) || ['total']

  const labels = keys
    .map(key => getMetricDisplayLabel(config.collection, { key }) || key)
    .filter(Boolean)

  return labels.join(', ') || 'Chart'
}

export const DATA_TYPE_LABELS = {
  table: 'Table',
  'line-chart': 'Line Chart',
  'bar-chart': 'Bar Chart',
  'pie-chart': 'Pie Chart',
  'funnel-chart': 'Funnel Chart',
}

export function isChartDataType(dataType) {
  return CHART_TYPES.some(type => type.value === dataType)
}

export function normalizeMetrics(metrics = []) {
  return metrics
    .map(metric => typeof metric === 'string' ? metric : metric?.key)
    .filter(Boolean)
}
